import { LanguageCode } from 'i18n/internationalization.types';
import LocalDateTime from 'ts-time/LocalDateTime';
import { ZoneId } from 'ts-time/Zone';
import ZonedDateTime from 'ts-time/ZonedDateTime';
import { TIMEZONES } from './constants/timezones';
import { orderBy } from './arrayUtils';

export const BROWSER_TIMEZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export function toZoneDateTimeBasedOnTimezone(
  dateTime: string,
  customTimeZone: string,
): ZonedDateTime {
  const date: LocalDateTime = LocalDateTime.parse(dateTime);
  return ZonedDateTime.ofDateTime(
    date,
    ZoneId.of(customTimeZone || BROWSER_TIMEZONE),
  );
}

/**
 * Returns the IANA code for the given `dateString` if it's in the format:
 * e.g:
 * '2025-11-18T15:00:00-06:00[America/Chicago]'
 */
export const getTimezoneId = ({
  dateString = new Date().toISOString(),
}: { dateString?: string | null } = {}): string | null => {
  if (!dateString) {
    return null;
  }

  try {
    const code = ZonedDateTime.parse(dateString).zone.id;

    return code !== 'Z' ? code : null;
  } catch {
    return null;
  }
};

/**
 * Retrieves the timezone offset in the format of: "GMT", "GMT-3", "GMT+2", etc.
 */
export const getTimezoneOffsetName = ({
  date = new Date(),
  language = LanguageCode.EN_US,
  timezone,
}: {
  date?: Date;
  language?: string;
  timezone: string;
}): string => {
  const formatter = new Intl.DateTimeFormat(language, {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  });

  const parts = formatter.formatToParts(date);
  const offsetName = parts.find((part) => part.type === 'timeZoneName')?.value;

  return offsetName ?? 'GMT';
};

/**
 * Retrieves the timezone offset both as a number and as the ISO string format (e.g "+01:00". "Z", "-03:00", etc)
 */
export const getTimezoneOffset = ({
  date = new Date(),
  language = LanguageCode.EN_US,
  timezone,
}: {
  date?: Date;
  language?: string;
  timezone: string;
}): { offset: number; offsetISO: string } => {
  const offsetName = getTimezoneOffsetName({ date, language, timezone });
  const match = offsetName.match(/GMT([+-])(\d+)(?::(\d+))?/);

  if (match) {
    const sign = match[1];
    const hours = parseInt(match[2], 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;
    const offset = (sign === '+' ? 1 : -1) * (hours + minutes / 60);

    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');
    const offsetISO = `${sign}${hoursStr}:${minutesStr}`; // ±HH:mm

    return { offset, offsetISO };
  }

  return { offset: 0, offsetISO: 'Z' };
};

/**
 * Retrieves the timezone name, e.g "Central European Time", "Pacific Daylight Time", etc.
 */
export const getTimezoneDescriptiveName = ({
  date = new Date(),
  language = LanguageCode.EN_US,
  timezone,
}: {
  date?: Date;
  language?: string;
  timezone: string;
}): string => {
  const longTimezoneNameParts = new Intl.DateTimeFormat(language, {
    timeZone: timezone,
    timeZoneName: 'long',
  }).formatToParts(date);

  const descriptiveName = longTimezoneNameParts.find(
    (s) => s.type === 'timeZoneName',
  )?.value;

  return descriptiveName ?? timezone;
};

/**
 * Given a timezone, this tries to find the closest match we have in our TIMEZONES list, based on the timezone offset
 * and the timezone name.
 */
export const findClosestTimezone = ({
  timezone: targetTimezone,
}: {
  timezone: string;
}): keyof typeof TIMEZONES => {
  const { offset: targetOffset } = getTimezoneOffset({
    timezone: targetTimezone,
  });

  const targetTimezoneDescriptiveName = getTimezoneDescriptiveName({
    timezone: targetTimezone,
  });

  const timezonesList = Object.values(TIMEZONES).map(({ timezone }) => ({
    timezone,
    // finds the difference between the target timezone offset and the other timezone.
    diff: Math.abs(targetOffset - getTimezoneOffset({ timezone }).offset),
    descriptiveName: getTimezoneDescriptiveName({ timezone }),
  }));

  const orderedTimezonesList = orderBy(timezonesList, [
    { field: 'diff', order: 'asc' },
    {
      field: ({ descriptiveName }) =>
        descriptiveName === targetTimezoneDescriptiveName,
      order: 'desc',
    },
  ]);

  // after the array gets ordered by diff and name, the first option should be the closest match.
  return orderedTimezonesList[0].timezone;
};
